// The storefront's router: pjax-max 5 (vendored from ../pjax-max by
// scripts/vendor-pjax-max.mjs; its types are in vendor/pjax-max/). It swaps
// the [data-router-view] inside <main data-router-wrapper> in
// layout/theme.liquid, and keeps the rest of the document in step through
// pjax-head.js. Links route through one delegated listener, so links added
// at any time do, inside shadow roots too.
//
// Not in the theme editor: sections there are re-rendered by Shopify, and a
// router would fight it.
//
// Diagnostics: run `localStorage.setItem("pjax-max:debug", "1")` and reload
// to load pjax-max's (they say in the console why a navigation fell back to
// a page load, which transition ran, and the like); removeItem to stop.

import { Core, Transition } from "@theme/pjax-max";
import { ThemeEvents } from "@theme/events";
import { getLenis } from "@theme/utilities";
import { shouldExcludeFromPjax } from "@theme/pjax-routing";
import { adoptFirstViewStylesheets, adoptViewStylesheets, createShopifyHead, runViewScripts } from "@theme/pjax-head";
import { kickApps, watchApps } from "@theme/pjax-apps";
import { images } from "@theme/image-scheduler";

const DEBUG_KEY = "pjax-max:debug";

// The scheduled images an unvisited page's first screen is likely to start with: liquid-mode
// ones, whose sizes the markup has, so a prefetch makes the same request their elements will.
// Not one in a <picture> (a phone takes its <source>), nor a product card's hover image.
const EARLY_IMAGES = 'img[data-mode="liquid"][data-srcset]:not(picture img, .product-card__image--second img)';

if (typeof window.Shopify === "undefined" || !window.Shopify.designMode) {
  initPjax().catch((error) => console.error("[pjax] failed to start", error));
}

async function initPjax() {
  const debug = wantsDebug() ? (await import("@theme/pjax-max-debug")).debug : undefined;
  const root = document.documentElement;
  const firstView = document.querySelector("[data-router-view]");

  // Routes that have to load in full (checkout, account, cart, app proxies:
  // see pjax-routing.js). pjax-max leaves a link alone when it or an ancestor
  // has data-router-disabled, which it reads as the click reaches its own
  // listener, on the document's bubble phase; this one is on the capture
  // phase, so it marks the link first. The theme's `on:click` handlers are
  // capture-phase too, and pjax-max skips a click they prevented.
  document.addEventListener(
    "click",
    (event) => {
      const link = event.composedPath().find((node) => node instanceof HTMLAnchorElement);
      if (!link?.hasAttribute("href")) return;
      if (shouldExcludeFromPjax(link.href)) {
        link.setAttribute("data-router-disabled", "excluded");
      } else if (link.getAttribute("data-router-disabled") === "excluded") {
        link.removeAttribute("data-router-disabled");
      }
    },
    true
  );

  // pjax-max swaps the view, never <body>, so the `{{ template.name }}` class
  // the layout puts on <body> (`body.index .slide__content` in theme.css)
  // would stay the first page's. The view's data-router-data carries the
  // template; track the class we own rather than diffing classList, since
  // other code adds and removes unrelated classes on <body>.
  let template = templateOf(firstView);
  const syncTemplateClass = ({ to }) => {
    const next = to.data.template;
    if (!next || next === template) return;
    if (template) document.body.classList.remove(template);
    document.body.classList.add(next);
    template = next;
  };

  const closeOverlays = () => {
    for (const dialog of document.querySelectorAll("dialog[open]")) {
      let host = dialog.parentElement;
      while (host && typeof host.closeDialog !== "function") host = host.parentElement;
      if (host) {
        try { host.closeDialog(); } catch {}
      } else {
        try { dialog.close(); } catch {}
      }
    }
  };

  // pjax-max restores scroll itself (manualScrollRestoration below): the top
  // or the #anchor for a link, where the page was left for back and forward.
  // Two things would undo its jumps. `html { scroll-behavior: smooth }` would
  // animate them, and the clamp as the old view leaves; it's set aside for
  // the length of a navigation. And Lenis, which smooth-scrolls the window
  // from its own rAF loop, would carry on an animation it had started; a stop
  // and start ends one (a Lenis the scroll lock holds stopped isn't
  // animating, and is left to the lock). Once the page is in, Lenis measures
  // it and takes up the position pjax-max left.
  const holdScroll = () => {
    root.style.scrollBehavior = "auto";
    const lenis = getLenis();
    if (lenis && !lenis.isStopped) {
      lenis.stop();
      lenis.start();
    }
  };
  const releaseScroll = () => {
    getLenis()?.resize();
    root.style.removeProperty("scroll-behavior");
  };

  // <page-transition> is the cover: it fades in on page:transition:start and
  // out on page:transition:end (page-transition.js). The out half is the base
  // one, which finishes at once; the in half removes the old view, then runs
  // the new one's scripts, which look things up in the document.
  class ThemeTransition extends Transition {
    async in({ from, to }) {
      from?.remove();
      runViewScripts(to);
    }
  }

  const dispatch = (type, detail = {}) =>
    document.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { ...detail, timestamp: Date.now() } }));

  // Images, as viewlines' pjax-max recipe has them (vendor/viewlines/skills/viewlines/recipes/).
  // The page being left lets go of its images with abort, so what it's still downloading doesn't
  // share the link with the next page's first screen. An unvisited page the prefetch already has
  // starts its first scheduled images as the navigation sets off; a page visited before has them
  // in the cache. The arriving view is said to be shown, so its images on screen start at once,
  // and again a frame later for the JS-mode ones, handed over once they've measured their sizes.
  const visited = new Set([pageOf(location.href)]);
  let early = null;
  const releaseEarly = () => {
    early?.release();
    early = null;
  };
  const leaveImages = ({ from, location: to, trigger }) => {
    images.release(from.view, { abort: true });
    releaseEarly();
    if (trigger === "popstate" || visited.has(pageOf(to.href))) return;
    const next = pjax.cache.get(pageOf(to.href));
    if (!next) return;
    const first = [...next.view.querySelectorAll(EARLY_IMAGES)].slice(0, 4);
    early = images.prefetch(
      first.map((img) => ({ srcset: img.dataset.srcset, sizes: img.getAttribute("sizes") ?? "" })),
      { priority: "soon" }
    );
  };
  const enterImages = ({ to, location: at }) => {
    visited.add(pageOf(at.href));
    images.showing(to.view);
    requestAnimationFrame(() => images.showing(to.view));
    // Its elements have joined what was asked for early by now.
    releaseEarly();
  };

  adoptFirstViewStylesheets(firstView);
  watchApps();

  const pjax = new Core({
    transitions: { default: ThemeTransition },
    head: createShopifyHead(),
    manualScrollRestoration: true,
    onLeave: [
      () => {
        document.body.classList.add("page-loading");
        closeOverlays();
        holdScroll();
        dispatch(ThemeEvents.pageTransitionStart, { direction: "out" });
      },
      leaveImages,
    ],
    onEnter: [
      (detail) => {
        syncTemplateClass(detail);
        adoptViewStylesheets(detail.to.view);
        document.body.classList.remove("page-loading");
      },
      enterImages,
    ],
    debug,
  });

  pjax.addEventListener("NAVIGATE_END", ({ detail }) => {
    releaseScroll();
    dispatch(ThemeEvents.pageTransitionEnd);
    dispatch(ThemeEvents.pageLoaded);
    kickApps(detail.to.view, detail.to.page);
  });

  window.__pjax = pjax;
  document.dispatchEvent(new CustomEvent("pjax:ready", { detail: { pjax } }));
}

/** A URL without its fragment: pjax-max's cache key. */
function pageOf(href) {
  return href.split("#")[0];
}

function templateOf(view) {
  try {
    return JSON.parse(view?.dataset.routerData || "{}").template ?? null;
  } catch {
    return null;
  }
}

function wantsDebug() {
  try {
    return localStorage.getItem(DEBUG_KEY) === "1";
  } catch {
    return false;
  }
}
